import { Button, Card, Skeleton, Stack, SvgIcon, Typography } from '@mui/material';
import Plus from '@untitled-ui/icons-react/build/esm/Plus';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { multiPayerFlag } from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { paths } from 'src/paths';
import { useCreateEocGate } from './use-create-eoc-gate';
import { getAllDisciplineTypeAction } from 'src/redux/actions/disciplineTypeAction';
import {
  getEnterprisePayerPlanType,
  getSinglePatient,
  switchTherapistDisciplineAction,
} from 'src/redux/actions/patientAction';
import { getEpisodeOfCare, resetEditEpisodeOfCare } from 'src/redux/actions/patientEpisodeOfCare';
import { getAllPatientPayersAction } from 'src/redux/actions/patientPayerActions';
import ListingTable from 'src/sections/dashboard/patients/patient-details-listing-table';
import { eocRowTestId, eocTestId } from 'src/components/common-modals/test-ids';

const { patients, patientDetails, episodeOfCare, details, addNewEpisodeOfCare } = paths;

const EpisodeOfCare = () => {
  const [rowPerPage, setRowPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const episodeOfCareData = useSelector((state: any) => state?.getEpisodeOfCare?.response);
  const episodeOfCareLoading = useSelector((state: any) => state?.getEpisodeOfCare?.loading);
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  // Segment B: Create-EOC gate migrated to the instrumented scopeKey path.
  const canCreateEoc = useCreateEocGate();
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const [isMultiPayer] = useFeatureToggleWhenReady(multiPayerFlag, 'FACILITY');
  const [providerId] = useCurrentUser(['providerId']);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      getEpisodeOfCare({
        patientId: getPatientId,
      })
    );
  }, [getPatientId]);

  const onRowClick = (episodeOfCareData: any) => {
    dispatch(switchTherapistDisciplineAction(''));
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareTitle: episodeOfCareData?.episodeTitle,
        episodeOfCareId: episodeOfCareData?.uuid,
        getPatientId,
        isMultiPayer,
      },
    });
  };

  const handleAddNewEpisodeOfCare = () => {
    dispatch(resetEditEpisodeOfCare());
    dispatch(
      getSinglePatient({
        patientId: getPatientId,
      })
    );
    dispatch(
      getAllDisciplineTypeAction({
        providerId,
      })
    );
    dispatch(getEnterprisePayerPlanType());
    dispatch(
      getAllPatientPayersAction({
        patientUuid: getPatientId,
        page: 0,
        pageSize: 1,
      })
    );
    navigate(`${patients}/${patientDetails}${episodeOfCare}${addNewEpisodeOfCare}`, {
      state: { getPatientId, isMultiPayer },
    });
  };

  return (
    <Card sx={{ padding: '1.5rem' }}>
      <Stack>
        {!episodeOfCareLoading && (
          <Stack
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'space-between'}
          >
            <Stack
              gap="1rem"
              alignItems="center"
              flexDirection="row"
              sx={{ marginBottom: '1rem' }}
            >
              <h3 style={{ margin: '0px' }}>Episode Of Care</h3>
              <Stack
                flexDirection="row"
                sx={{
                  borderRadius: '12px',
                  display: 'flex',
                  border: '1px solid #00000042',
                  color: '#111927',
                  padding: '3px 10px',
                }}
              >
                <Typography variant="subtitle2">{`${episodeOfCareData?.totalElements} Episodes Of Care`}</Typography>
              </Stack>
            </Stack>
            {patientData?.active && (
              <Stack>
                <Button
                  color="inherit"
                  startIcon={
                    <SvgIcon>
                      <Plus />
                    </SvgIcon>
                  }
                  onClick={handleAddNewEpisodeOfCare}
                  disabled={!canCreateEoc}
                  data-testid={eocTestId('add-new')}
                  sx={{ color: 'primary.dark' }}
                >
                  Add New Episode of Care
                </Button>
              </Stack>
            )}
          </Stack>
        )}

        {!episodeOfCareLoading ? (
          <>
            {episodeOfCareData?.content?.length > 0 ? (
              <ListingTable
                items={episodeOfCareData?.content}
                count={episodeOfCareData?.totalElements}
                page={page}
                onRowClick={onRowClick}
                rowTestId={(episode: any) => eocRowTestId('row', episode?.uuid)}
                rowsPerPage={rowPerPage}
                tableHead={[
                  {
                    label: 'NAME',
                    value: 'episodeTitle',
                  },
                  {
                    label: 'RESIDENCE',
                    value: 'disciplineOfComplete',
                  },
                  {
                    label: 'SCREENING TYPE',
                    value: 'screeningTypes',
                  },
                  {
                    label: 'PAYER NAME',
                    value: 'payerName',
                  },
                  {
                    label: 'START DATE',
                    value: 'startDate',
                  },
                  {
                    label: 'END DATE',
                    value: 'endDate',
                  },
                  {
                    label: 'Status',
                    value: 'status',
                    action: 'episodeOfCare',
                  },
                  {
                    label: '',
                    value: 'arrow',
                  },
                ]}
              />
            ) : (
              <Typography sx={{ margin: '1rem auto' }}>No Record Founds</Typography>
            )}
          </>
        ) : (
          <>
            {Array(12)
              .fill(null)
              .map((_, index) => (
                <Skeleton key={index} />
              ))}
          </>
        )}
      </Stack>
    </Card>
  );
};

export default EpisodeOfCare;
